import type { CommissionSightClient } from '@commissionsight/sdk';
import { useCallback } from 'react';
import { Badge, ErrorNote, Metric, Panel } from '../components/ui.js';
import { fmtWhen } from '../lib/format.js';
import { useAsyncData } from '../lib/useAsyncData.js';

const fmtDuration = (ms: number) => (ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`);

/**
 * System tab — operational read on scheduled-maintenance (cron) runs. There's no
 * separate scheduler store; each cron pass writes a row to D1 (cron_runs) and this
 * surfaces them, alongside a description of what the schedule does.
 */
export function System({ client }: { client: CommissionSightClient }) {
  const { data, error, reload } = useAsyncData(
    useCallback(() => client.admin.cronRuns({ limit: 30 }), [client]),
  );

  const last = data?.runs[0] ?? null;
  const sum = (pick: (r: NonNullable<typeof data>['runs'][number]) => number) =>
    (data?.runs ?? []).reduce((acc, r) => acc + pick(r), 0);

  return (
    <div>
      <Panel title="Scheduled maintenance">
        {error ? (
          <ErrorNote message={error} onRetry={reload} />
        ) : !data ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Metric
                label="Last run"
                value={last ? fmtWhen(last.ranAt) : '—'}
                tone={last ? 'emerald' : 'neutral'}
                hint={last ? fmtDuration(last.durationMs) : 'no runs yet'}
              />
              <Metric
                label="Files purged"
                value={sum((r) => r.filesPurged)}
                tone="indigo"
                hint="last 30 runs"
              />
              <Metric
                label="Jobs canceled"
                value={sum((r) => r.jobsCanceled)}
                tone="amber"
                hint="purge retention"
              />
              <Metric
                label="Webhooks redelivered"
                value={sum((r) => r.webhooksRedelivered)}
                tone="sky"
                hint="last 30 runs"
              />
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {data.tasks.map((t) => (
                <div key={t.key} className="rounded-lg border border-neutral-200/70 bg-white p-3">
                  <div className="text-sm font-medium text-neutral-800">{t.title}</div>
                  <p className="mt-0.5 text-xs text-neutral-500">{t.description}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </Panel>

      {data && (
        <Panel title="Run history">
          {data.runs.length === 0 ? (
            <p className="text-sm text-neutral-500">
              No maintenance runs recorded yet — the cron writes a row each time it fires.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-neutral-500">
                <tr>
                  <th className="py-1">Ran</th>
                  <th>Requeued</th>
                  <th>Webhooks</th>
                  <th>Purged</th>
                  <th>Canceled</th>
                  <th>Billed</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {data.runs.map((r) => (
                  <tr key={r.id} className="border-t border-neutral-100">
                    <td className="py-2 text-neutral-700">{fmtWhen(r.ranAt)}</td>
                    <td className="tabular-nums">{r.requeued}</td>
                    <td className="tabular-nums">{r.webhooksRedelivered}</td>
                    <td className="tabular-nums">
                      {r.filesPurged > 0 ? <Badge tone="indigo">{r.filesPurged}</Badge> : 0}
                    </td>
                    <td className="tabular-nums">
                      {r.jobsCanceled > 0 ? <Badge tone="amber">{r.jobsCanceled}</Badge> : 0}
                    </td>
                    <td className="tabular-nums">{r.billed}</td>
                    <td className="tabular-nums text-neutral-400">{fmtDuration(r.durationMs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}
    </div>
  );
}
